/**
 * Icons. Plan §2.1 specifies `lucide-react` at stroke 1.6–1.7 to match the
 * mockups; this module fixes the stroke and size in one place so no call site
 * has to remember.
 */
import type { ReactElement } from 'react'
import { cn } from '../lib/cn.js'
import type { FieldIcon } from '@rowted/shared'
import {
  Eye,
  AlarmClock, ArrowDownUp, ArrowRight, ArrowUp, Baseline, Bell, Bold, Briefcase, Building2,
  CalendarClock, Calendar,
  CheckSquare, Highlighter, Italic, List,
  ALargeSmall, IndentDecrease, IndentIncrease, ListOrdered, Strikethrough, Underline,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, CircleDot, Info, Columns3, DollarSign, Download, FileText,
  Clock, ExternalLink, FileSignature, Flag, GitBranch, Grid2x2, GripVertical, Group, Hash, Home,
  Image as ImageIcon,
  Layers, Link2, Filter, Mail, Map, Repeat,
  MousePointer2, Hand, Pentagon, Square, Circle as CircleShape, Spline, Type, Undo2,
  MapPin, Menu, MessageSquare, MoreHorizontal, MousePointerClick, Navigation, Package,
  Paperclip, Pencil, Copy, Save, Trash2,
  BarChart3, Lock, MoreVertical, Percent, Phone, Plus, ReceiptText, Route, Rows3, Ruler, Search,
  Send, ShoppingCart,
  Sigma, Settings2, Sparkles, StickyNote, Tag, Target, Timer, TrendingUp, Truck, Upload, User,
  Users, Wrench, X,
  Bell as BellIcon, Clock as ClockIcon, Database, Globe, History, LayoutDashboard, LayoutGrid,
  Network, Plug, Shapes, Shield, UsersRound, Wand2,
  Boxes, CircleDollarSign, Hammer, Handshake,
  Home as OrgBranch, Warehouse,
  KeyRound, LogOut, Monitor, Moon, Settings as SettingsCog, ShieldCheck, UserRound,
  TriangleAlert, Camera, Check, LoaderCircle,
  HeartPulse, Activity, Fingerprint,
  LocateFixed, Minus,
  ArrowDownToLine, ArrowUpFromLine, CalendarCheck, CornerDownRight,
  type LucideIcon,
} from 'lucide-react'

export interface IconProps {
  size?: number
  className?: string
}

/*
 * `shrink-0`, on every icon in the product.
 *
 * An icon is a flex item wherever it is used, and a flex item's default is to
 * shrink rather than overflow. So an icon in a box that is a few pixels too
 * narrow does not spill or clip — it silently squashes, keeping its height and
 * losing its width, which reads as a *smaller icon* rather than as a layout
 * fault. That is what the list toolbar's gear was: a 17px glyph painted 10px
 * wide, next to 15px ones at their full width.
 *
 * A glyph's size is the number its call site passes. Nothing else may change
 * it, so nothing else may shrink it.
 */
function make(Component: LucideIcon) {
  return function Icon({ size = 18, className }: IconProps) {
    return (
      <Component
        size={size}
        strokeWidth={1.7}
        className={cn('shrink-0', className)}
        aria-hidden
      />
    )
  }
}

export const IconBell = make(Bell)
export const IconChevronDown = make(ChevronDown)
export const IconChevronUp = make(ChevronUp)
export const IconInfo = make(Info)
export const IconChevronLeft = make(ChevronLeft)
export const IconChevronRight = make(ChevronRight)
export const IconCalendar = make(Calendar)
export const IconPin = make(MapPin)
export const IconSearch = make(Search)
export const IconSparkles = make(Sparkles)

/** The list controls — plan §3.5's row of Filter, Group, Sort, Fields and Σ. */
export const IconFilter = make(Filter)
export const IconGroup = make(Group)
export const IconSort = make(ArrowDownUp)
export const IconFields = make(Columns3)
export const IconSigma = make(Sigma)
export const IconDensity = make(Rows3)
/**
 * List settings, and the user menu's Settings row.
 *
 * A cog, not a hamburger and not the sliders. The hamburger said "there is a
 * menu here"; the question people actually have at that spot is "where do I
 * change how this is set up", and a cog is the one glyph everybody already
 * reads that way. Nineteen mockups draw it and not one draws the sliders —
 * those stay for `IconCatalogSetup`, which is a different idea wearing the
 * same word.
 */
export const IconSettings = make(SettingsCog)
/** One person, where `NAV_ICONS.contacts` is a group of them. */
export const IconPerson = make(User)
/*
 * One arrow, pointing down: Export is the only thing on the toolbar that moves
 * a file at all, and every mockup that draws it draws a download.
 *
 * There used to be a second arrow beside it, and a comment here arguing about
 * which way round the pair should point — Export as an upload because data
 * leaves Rowted, Import as a download because it arrives. The argument is
 * moot now. Import is not an arrow: it is `proposeMapping` reading a
 * spreadsheet and guessing what its columns mean, so it wears a wand and says
 * **Smart import** (`packages-catalog.html`, `company-org-divisions.html`).
 */
export const IconExport = make(Download)
export const IconSmartImport = make(Wand2)
export const IconPlus = make(Plus)
export const IconClose = make(X)
export const IconTag = make(Tag)
export const IconMenu = make(Menu)
export const IconGrip = make(GripVertical)
/** Duplicate a row — the advanced builder's "the same but for York". */
export const IconCopy = make(Copy)
/**
 * Delete, on a row that says so in words beside it.
 *
 * Nothing in Rowted deletes (rule 2) — this is for the rows that *archive*
 * or soft-delete and say which, like a map layer's `Delete layer…`, where
 * the confirm names the shape count and offers to move them instead.
 */
export const IconTrash = make(Trash2)
/** Save as view. */
export const IconSave = make(Save)
/** Copy a link to this filter. */
export const IconLink = make(Link2)
export const IconEdit = make(Pencil)
/* Preview — the customer's view of a document, on a phone's header. */
export const IconEye = make(Eye)
export const IconSend = make(Send)
/* A measurement — a figure somebody walked the site for. */
export const IconRuler = make(Ruler)
export const IconNote = make(StickyNote)
/* The six activity kinds (record page §4). */
export const IconTask = make(CheckSquare)
export const IconCall = make(Phone)
export const IconMeeting = make(Calendar)
export const IconAttachment = make(Paperclip)

/*
 * The Files tab (record page §7).
 *
 * `IconUpload` is the same glyph as `IconExport` on purpose. Export and upload
 * are the same arrow, and the two names exist so a call site says which one it
 * means — a Files tab whose button imported `IconExport` would be read as a
 * download by the next person to open the file.
 */
export const IconUpload = make(Upload)
export const IconGrid = make(Grid2x2)
export const IconRows = make(Rows3)
export const IconFile = make(FileText)
export const IconImage = make(ImageIcon)
export const IconExternal = make(ExternalLink)
/* The arrow on a Navigate button — a heading, not a road. */
export const IconNavigate = make(Navigation)
/** What happens next — the sentence the board's Next action column prints. */
export const IconNext = make(ArrowRight)
/** Back to the top of a long phone list (`docs/phone-list-loading.md` §4). */
export const IconArrowUp = make(ArrowUp)
/**
 * The only spinner in the product, and it spins here rather than at each of
 * its call sites — a loading mark that is still on half the screens that use
 * it is a loading mark that reads as a broken icon.
 */
export function IconSpinner({ size = 16, className }: IconProps) {
  return (
    <LoaderCircle
      size={size}
      strokeWidth={1.7}
      aria-hidden
      className={cn('animate-spin', className)}
    />
  )
}
/** A quote: a document somebody signs, which is what tells it from an invoice. */
export const IconQuote = make(FileSignature)

/**
 * The glyphs a record page's tabs need that are neither nav items nor column
 * headers.
 *
 * A third set rather than three more entries in `NAV_ICONS`: that one is the
 * left nav, and an Overview tab is not a nav destination. `record-tabs.tsx`
 * and the phone tab bar look here first, then at the nav, then at the column
 * icons, so a tab can name a glyph from whichever vocabulary it belongs to.
 */
export const RECORD_TAB_ICONS = {
  home: make(Home),
  /*
   * The visit page's own six (`docs/visit-page.md` §7.1: Materials
   * `material`, Equipment `loader`, Time `hourglass`, Money `dollar`, For the
   * crew `clipboard`, From the crew `camera`) — the board's own paths
   * (folder 50, Board 1's tab row), through `drawn()` below, so the row draws
   * the board's pictures rather than lucide's nearest (⛔).
   */
  material: drawn(<><circle cx="12" cy="7" r="3" /><circle cx="7" cy="16" r="3" /><circle cx="17" cy="16" r="3" /><path d="M12 10v2M9 14l1-2M15 14l-1-2" /></>),
  loader: drawn(<><path d="M4 17h12M6 17V9h6l3 4M15 13h5l-1 4" /><circle cx="7" cy="18" r="1.8" /><circle cx="16" cy="18" r="1.8" /></>),
  hourglass: drawn(<path d="M6 3h12M6 21h12M7 3c0 5 5 6 5 9s-5 4-5 9M17 3c0 5-5 6-5 9s5 4 5 9" />),
  dollar: drawn(<path d="M12 3v18M16 7.5c-.8-1.2-2.3-2-4-2-2.2 0-4 1.1-4 3s1.8 2.6 4 3 4 1.1 4 3-1.8 3-4 3c-1.9 0-3.4-.9-4.2-2.2" />),
  clipboard: drawn(<><rect x="6" y="4" width="12" height="17" rx="2" /><path d="M9 4V3h6v1M9 10h6M9 14h6M9 18h3" /></>),
  camera: drawn(<><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3.5" /></>),
  files: make(Paperclip),
  bell: make(Bell),
  history: make(Clock),
  /*
   * The catalog's own two. The same glyphs the quote builder's rows use — a
   * box for a package, layers for a service — because a tab and the row it
   * leads to naming one thing with two pictures is how a vocabulary rots.
   */
  package: make(Package),
  service: make(Layers),
  /*
   * The organization page's four. Its tabs are not a record's tabs, but they
   * are drawn by the same row, so their glyphs live in the same set rather
   * than being reached for out of lucide at the call site.
   */
  details: make(List),
  yards: make(Warehouse),
  access: make(Shield),
  documents: make(FileText),
  /*
   * A property's Map tab (`property-maps-v2.md` §2.1). The same folded-map
   * glyph the settings rail's Maps row wears and the mockup's Create-a-map
   * tile draws, because a tab and the thing it leads to naming one feature
   * with two pictures is how a vocabulary rots.
   */
  map: make(Map),
} as const

/**
 * The map editor's tool rail — `property-maps-v2.md` §5.3, board 2.
 *
 * Its own set rather than nine loose imports at the call site, for the reason
 * `RECORD_TAB_ICONS` is a set: the rail, the keyboard table and the phone's
 * drawing bar all name the same tools, and a glyph reached for out of lucide
 * in three places is three chances for them to disagree.
 */
/* The canvas's own controls: zoom in, zoom out, and back to the property. */
export const IconMinus = make(Minus)
export const IconLocate = make(LocateFixed)
/* The phone editor's own two: how many layers, and take that back. */
export const IconLayers = make(Layers)
/**
 * The loop a recurring visit wears (`docs/proposal.md` §6.1).
 *
 * The same shape `RecurringBadge` draws on a job's mark, so a weekly visit on
 * a proposal and a weekly job on the board say "this comes round again" with
 * one glyph rather than two near-identical ones.
 */
export const IconLoop = make(Repeat)
export const IconUndo = make(Undo2)

export const MAP_TOOL_ICONS = {
  select: make(MousePointer2),
  pan: make(Hand),
  area: make(Pentagon),
  rect: make(Square),
  circle: make(CircleShape),
  line: make(Spline),
  marker: make(MapPin),
  label: make(Type),
  measure: make(Ruler),
  undo: make(Undo2),
} as const

/*
 * The note toolbar.
 *
 * The glyphs everybody has pressed a thousand times in Word: a bold B, a
 * slanted I, a bulleted list, a highlighter pen, and an A sitting on a bar in
 * the colour it would write in. `IconTextColor` draws only the A and its
 * baseline — the colour bar is `note-editor.tsx`'s, because the whole point of
 * that icon is that the bar is not the icon's colour.
 */
export const IconBold = make(Bold)
export const IconItalic = make(Italic)
export const IconBulletList = make(List)
export const IconHighlight = make(Highlighter)
export const IconTextColor = make(Baseline)
/* The rest of a task's notes toolbar (Aaron, Oct 25, note 1.4). */
export const IconUnderline = make(Underline)
export const IconStrike = make(Strikethrough)
export const IconNumberedList = make(ListOrdered)
export const IconIndent = make(IndentIncrease)
export const IconOutdent = make(IndentDecrease)
export const IconTextSize = make(ALargeSmall)

/*
 * Email and text message. The grid drew these as "✉" and "💬" — a glyph whose
 * shape is whatever font happened to load, and an emoji that renders in full
 * colour on a mac and as an outline on Windows. These are the same family, at
 * the same stroke, as everything else on the screen.
 */
export const IconEmail = make(Mail)
export const IconSms = make(MessageSquare)
/** Posted, in an envelope, by a person in a van. */
export const IconMail = make(Package)

/**
 * A package on a quote: several trips sold as one thing.
 *
 * The same box `IconMail` uses, because it is the same idea — a thing that
 * arrives, gets used and goes away again — and a second box glyph would be
 * two drawings of one concept.
 */
export const IconPackage = make(Package)

/** A trip that takes the units away again, timed by a clock rather than a date. */
export const IconPickup = make(Clock)

/*
 * A visit's When and its kind (visits contract §4.5, Board 8). Board 8 draws
 * a pick-up as an arrow lifting off a line and a drop-off as one coming down
 * to it, which is not the rental's clock above — that one says *when* the
 * units go, these say *what the trip does*.
 */
/** ↳ — a visit that follows another. */
export const IconFollows = make(CornerDownRight)
export const IconDropOff = make(ArrowDownToLine)
export const IconPickUp = make(ArrowUpFromLine)
/** A visit's tile on the When popover — a calendar with a tick. */
export const IconVisitDay = make(CalendarCheck)
/** Ends · After N visits. */
export const IconCount = make(Hash)

/**
 * One glyph per *record kind* — the single answer to "what does a contact
 * look like".
 *
 * The nav rail, the record flyouts' header tiles, the builder's line tiles
 * and a lookup row all draw a contact, a property, a job. They used to each
 * pick their own: the rail drew two people for Contacts and the flyout drew
 * one; the rail drew a map pin for Properties and the flyout drew a house.
 * Nothing was wrong with either glyph, and that was the problem — the same
 * thing wore a different face depending on which screen you were on.
 *
 * `NAV_ICONS` reads from here, and so does `KindTile`, so there is one place
 * to change a record's face and no way for two screens to disagree.
 */
export const RECORD_ICONS = {
  contact: Users,
  property: MapPin,
  job: Briefcase,
  visit: Calendar,
  crew: UsersRound,
  equipment: Truck,
  invoice: FileText,
  purchasing: ShoppingCart,
  service: Layers,
  package: Package,
  item: Boxes,
  /*
   * A hammer, not a hard hat. At the 13px a builder row draws it, a hard hat
   * is a dome with a brim and reads as a bell — Aaron called it "the little
   * bell icon for labor" without ever having been told what it was meant to
   * be, which is the whole verdict on it. Not a wrench either: `Wrench` is
   * already equipment service's, and no two kinds share a glyph.
   */
  labor: Hammer,
  /*
   * A fee is money, and it is not a truck.
   *
   * `deployment` and `equipment` were both `Truck`, separated by colour
   * alone, so the two kinds a quote most often mixes were the same picture
   * twice. They are different things: equipment is the machine that turns
   * up, a fee is what is charged for turning up.
   */
  deployment: CircleDollarSign,
  sub: Handshake,

  /*
   * The organization (0075): a company, the regions and districts that group
   * it, the branches where work happens, and the two things that hang off a
   * branch. Here rather than drawn per screen, so the tree, the dialogs, the
   * phone list and the scope picker all wear one face per kind.
   */
  company: Building2,
  region: Globe,
  district: Map,
  branch: OrgBranch,
  yard: Warehouse,
  zone: Shapes,
} as const

export type RecordKind = keyof typeof RECORD_ICONS

/**
 * One icon per nav item, matching `NAV_ITEMS` in /shared/permissions.ts and the
 * glyphs drawn in `design/mockups/02-left-navigation--frame/nav-main.png`.
 * Visits and Invoices are deliberately different icons — they were the same in
 * an earlier pass and the rail read as two identical rows.
 */
/**
 * A glyph drawn from a board's own SVG, at the same size and stroke as
 * `make()` gives a lucide icon — for the few the boards draw differently
 * (`docs/activity.md` §13.4: Activity's pulse, its calendar, inbox and task).
 */
function drawn(children: ReactElement, defaultStroke = 1.7) {
  return function Icon({ size = 18, className }: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
        strokeWidth={size <= 16 ? 2 : defaultStroke} strokeLinecap="round" strokeLinejoin="round"
        className={cn('shrink-0', className)} aria-hidden
      >
        {children}
      </svg>
    )
  }
}

/** Board 1: the pulse — the nav row and the *All activity* tab. */
export const IconActivity = drawn(<path d="M22 12h-4l-3 9L9 3l-3 9H2" />)
/** Board 1: the *Calendar* tab. */
export const IconActivityCalendar = drawn(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>)
/** Board 1: the *Inbox* tab. */
export const IconInbox = drawn(<><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>)
/** Board 1: the *Tasks* tab. */
export const IconTaskTab = drawn(<><rect x="3" y="3" width="18" height="18" rx="4" /><path d="M8 12.5l3 3 5-6" /></>)

export const NAV_ICONS = {
  activity: IconActivity,
  calendar: IconActivityCalendar,
  inbox: IconInbox,
  task: IconTaskTab,
  contacts: make(RECORD_ICONS.contact),
  properties: make(RECORD_ICONS.property),
  jobs: make(RECORD_ICONS.job),
  visits: make(RECORD_ICONS.visit),
  schedule: make(RECORD_ICONS.visit),
  purchasing: make(RECORD_ICONS.purchasing),
  invoices: make(RECORD_ICONS.invoice),
  equipment: make(RECORD_ICONS.equipment),
  equipmentService: make(Wrench),
} as const


/**
 * One glyph per field icon, so a column header can be read by shape.
 *
 * Keyed by the vocabulary in /shared rather than by field name: the server
 * says what kind of thing a column holds, and this says what that looks like.
 * A screen that picked its own glyph would eventually disagree with the other
 * screen showing the same column.
 *
 * Stroke 2 here rather than the 1.7 everywhere else — v2 asks for it, and at
 * 14px in a header the lighter stroke reads as a smudge.
 */
function header(Component: LucideIcon) {
  return function Icon({ size = 14, className }: IconProps) {
    return <Component size={size} strokeWidth={2} className={className} aria-hidden />
  }
}

export const COLUMN_ICONS: Record<FieldIcon, (props: IconProps) => ReactElement> = {
  text: header(FileText),
  user: header(User),
  company: header(Building2),
  money: header(DollarSign),
  number: header(Hash),
  percent: header(Percent),
  date: header(Calendar),
  status: header(CircleDot),
  tag: header(Tag),
  phone: header(Phone),
  email: header(Mail),
  message: header(MessageSquare),
  place: header(MapPin),
  job: header(Briefcase),
  boolean: header(CheckSquare),
  action: header(MousePointerClick),
  count: header(Layers),

  /*
   * The second pass — what a column is about rather than what type it holds.
   * A board where the job number, the job, the customer, the property and the
   * salesperson all wore the same sheet of paper was a board you had to read
   * word by word, which is the thing the icons were added to stop.
   */
  reference: header(Hash),
  hours: header(Timer),
  map: header(Map),
  route: header(Route),
  crew: header(Users),
  equipment: header(Truck),
  invoice: header(ReceiptText),
  quote: header(FileText),
  note: header(StickyNote),
  clock: header(AlarmClock),
  calendar_due: header(CalendarClock),
  branch: header(GitBranch),
  category: header(Grid2x2),
  unit: header(Ruler),
  link: header(Link2),
  flag: header(Flag),
}

/** The ⋯ that opens a header's or a row's menu. */
export const IconMore = make(MoreHorizontal)
/** The ⋮ on a card's own header, where ⋯ is a row's menu in a table. */
export const IconMoreVertical = make(MoreVertical)

/*
 * The landing marks (v2 principle 1). A machine for equipment, a receipt for
 * an invoice — the same family and stroke as everything else, so a column of
 * marks reads as part of the product rather than as clip art.
 */
export const IconMachine = make(Truck)

/** The card builder's four shapes, its three audiences, and its own company. */
export const IconNumber = make(Sigma)
export const IconTrend = make(TrendingUp)
export const IconBars = make(BarChart3)
export const IconBullseye = make(Target)
export const IconLock = make(Lock)
export const IconTeam = make(Users)
export const IconCompany = make(Building2)
export const IconReceipt = make(ReceiptText)


/*
 * One glyph per settings page (`design-remediation.md` §1.2, drawn in
 * `21-catalog-packages/html/packages-settings-home.html`).
 *
 * Named after the page rather than the picture, because the nav row and the
 * home card have to wear the same mark and a rename of the page should not
 * leave two call sites disagreeing about which drawing it was.
 */
export const IconProfileBranding = make(Building2)
export const IconOrganization = make(Network)
export const IconDivisions = make(Shapes)
export const IconUsersRoles = make(Users)
/** Settings › Customers › Lead sources (`new-request.md` §14.1: icon *flag*). */
export const IconLeadSources = make(Flag)
export const IconCrews = make(UsersRound)
export const IconTimePin = make(ClockIcon)
export const IconFeatures = make(Sparkles)
export const IconCatalog = make(LayoutGrid)
export const IconCatalogSetup = make(Settings2)
export const IconPricing = make(Percent)
export const IconBillingPlans = make(DollarSign)
export const IconInvoicingFees = make(ReceiptText)
export const IconAccounting = make(Plug)
export const IconTemplates = make(BellIcon)
export const IconPortal = make(Globe)
export const IconBoardDefaults = make(LayoutDashboard)
export const IconSuggestions = make(Wand2)
export const IconMaps = make(Map)
export const IconTransfer = make(Database)
export const IconAudit = make(History)
export const IconSecurity = make(Shield)

/** My account's Security and Preferences rows (WP2's `SettingRow` icons). */
export const IconShieldCheck = make(ShieldCheck)
export const IconPinCode = make(KeyRound)
export const IconMonitor = make(Monitor)
/** The tile on a passkey's setup card and on its sign-in panel (board 3). */
export const IconFingerprint = make(Fingerprint)
export const IconMoon = make(Moon)
export const IconHome = make(Home)
export const IconUserRound = make(UserRound)
export const IconSignOut = make(LogOut)
/*
 * The triangle a field's error line wears (`flyout-field-kit.html`).
 *
 * A message in red is already a message in red; the glyph is what makes it
 * legible to somebody who cannot tell the red from the muted grey beside it,
 * which is the whole reason the mockup draws one.
 */
export const IconWarn = make(TriangleAlert)
/** The tick inside a chosen chip. */
export const IconCheck = make(Check)
/** The tile on a photo drop target. */
export const IconCamera = make(Camera)
export const IconClock = make(ClockIcon)

/*
 * Rowted Admin's rail — `docs/rowted-admin-v2.md` §4.1.
 *
 * Named for what they point at rather than reusing the office app's names:
 * `IconCompany` is a customer's own company, `IconClients` is every customer
 * Rowted has, and a reader should not have to know they are the same glyph.
 */
export const IconClients = make(Building2)
export const IconIntegrations = make(Plug)
export const IconAdminUsers = make(Users)
export const IconHealth = make(HeartPulse)
export const IconAuditLog = make(Activity)
